import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { storesApi } from '@/api';
import { useMasterData } from '@/hooks/useMasterData';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { PlanLimitNotice } from '@/components/ui/PlanLimitNotice';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { MasterLocaleSelects } from '@/components/master/MasterLocaleSelects';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { StoreRequest, StoreResponse } from '@/types/store';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: StoreRequest = {
  name: '',
  code: '',
  countryCode: 'IN',
  currencyCode: 'INR',
  timezone: 'Asia/Kolkata',
  taxMode: 'GST',
  taxInclusive: true,
};

export function StoresPage() {
  const { taxModes, loading: metaLoading } = useMasterData();
  const { atStoreLimit, storeLimitMessage, reload: reloadLimits } = usePlanLimits();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<StoreResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StoreResponse | null>(null);
  const [form, setForm] = useState<StoreRequest>(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await storesApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<StoreResponse>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'city', header: 'City' },
    { key: 'countryCode', header: 'Country' },
    { key: 'currencyCode', header: 'Currency' },
    { key: 'taxMode', header: 'Tax' },
    { key: 'contactNumber', header: 'Phone' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (r.status ? 'Active' : 'Inactive'),
    },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600"
          onClick={() => {
            setEditing(r);
            setForm({
              name: r.name,
              code: r.code,
              address: r.address,
              city: r.city,
              contactNumber: r.contactNumber,
              email: r.email,
              countryCode: r.countryCode,
              stateCode: r.stateCode,
              currencyCode: r.currencyCode,
              timezone: r.timezone,
              taxMode: r.taxMode,
              taxInclusive: r.taxInclusive,
            });
            setModalOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await storesApi.update(editing.id, form);
      else await storesApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
      reloadLimits();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Stores"
        breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Stores' }]}
        actions={
          <Button
            disabled={atStoreLimit}
            title={atStoreLimit ? storeLimitMessage ?? '' : undefined}
            onClick={() => { setEditing(null); setForm(empty); setModalOpen(true); }}
          >
            <AddIcon fontSize="small" /> Add Store
          </Button>
        }
      />
      {atStoreLimit && storeLimitMessage && <PlanLimitNotice message={storeLimitMessage} />}
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Store' : 'New Store'}
        footer={<><Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button><Button onClick={save} loading={saving}>Save</Button></>}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="City" value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <Input label="Phone" value={form.contactNumber ?? ''} onChange={(e) => setForm({ ...form, contactNumber: e.target.value })} />
          <MasterLocaleSelects
            className="sm:col-span-2"
            value={{
              countryCode: form.countryCode ?? '',
              currencyCode: form.currencyCode ?? '',
              timezone: form.timezone ?? '',
            }}
            onChange={(patch) => setForm({ ...form, ...patch })}
            countryRequired
            currencyRequired
            timezoneRequired
          />
          <Input
            label="State code"
            value={form.stateCode ?? ''}
            onChange={(e) => setForm({ ...form, stateCode: e.target.value })}
          />
          <Select
            label="Tax mode *"
            disabled={metaLoading}
            placeholder="Select tax mode"
            options={taxModes.map((t) => ({ value: t.code, label: t.label }))}
            value={form.taxMode ?? ''}
            onChange={(v) => setForm({ ...form, taxMode: v })}
          />
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={form.taxInclusive ?? false}
              onChange={(e) => setForm({ ...form, taxInclusive: e.target.checked })}
            />
            Tax inclusive (store default)
          </label>
        </div>
      </Modal>
    </div>
  );
}
